<template>
  <div class="app">

    <header class="header">
      <h1>Smartcontracts Admin</h1>
      <div class="wallet-status">
        <span class="status-dot" :class="account ? 'connected' : 'disconnected'"></span>
        <span v-if="chainId" class="network-chip">{{ networkLabel }}</span>
        <button v-if="!account" class="btn btn-primary" :disabled="connecting" @click="connectWallet">
          {{ connecting ? 'Waiting…' : 'Connect MetaMask' }}
        </button>
        <span v-else class="account-addr">{{ shortAddr(account) }}</span>
      </div>
    </header>

    <div v-if="!account" class="empty-state">
      <p>Connect MetaMask to continue</p>
      <p v-if="connectError" class="connect-error">{{ connectError }}</p>
    </div>

    <div v-else class="sections">

      <p class="info-banner">
        To switch network or account, change it in MetaMask. This page will pick up the change.
      </p>

      <section class="card">
        <div class="card-header" @click="toggle('settings')">
          <div>
            <h2>⚙️ Contract Addresses</h2>
            <p class="card-subtitle">Storage and Proxy addresses for all operations below</p>
          </div>
          <span class="chevron">{{ open.settings ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.settings" class="card-body">
          <div class="field">
            <label>Storage</label>
            <input v-model="cfg.storage" placeholder="0x..." />
          </div>
          <div class="field">
            <label>Proxy</label>
            <input v-model="cfg.proxy" placeholder="0x..." />
          </div>
          <button class="btn btn-primary" @click="saveConfig">Save & reload</button>
        </div>
        <div v-else class="card-footer">
          <span class="addr-chip">Storage: {{ currentStorage ? shortAddr(currentStorage) : '⚠️ not set' }}</span>
          <span class="addr-chip">Proxy: {{ currentProxy ? shortAddr(currentProxy) : '⚠️ not set' }}</span>
        </div>
      </section>

      <section class="card">
        <div class="card-header" @click="toggle('whitelist')">
          <div>
            <h2>📋 Whitelist</h2>
            <p class="card-subtitle">Who can call the Proxy → Storage.grantAccess / revokeAccess</p>
          </div>
          <span class="chevron">{{ open.whitelist ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.whitelist" class="card-body">
          <div class="field">
            <label>Address</label>
            <input v-model="wl.address" placeholder="0x..." />
          </div>
          <div class="row-btns">
            <button class="btn btn-success" :disabled="busy.grant" @click="grantAccess">
              {{ busy.grant ? '…' : '✅ Grant access' }}
            </button>
            <button class="btn btn-danger" :disabled="busy.revoke" @click="revokeAccess">
              {{ busy.revoke ? '…' : '🚫 Revoke access' }}
            </button>
            <button class="btn btn-secondary" :disabled="busy.check" @click="checkAccess">
              {{ busy.check ? '…' : '🔍 Check' }}
            </button>
          </div>
          <div v-if="wl.checkResult !== null" class="result" :class="wl.checkResult ? 'ok' : 'err'">
            {{ wl.address }} — {{ wl.checkResult ? '✅ allowed' : '❌ not allowed' }}
          </div>
          <feedback :chain-id="txChains.grant" :tx="txs.grant"  :err="errs.grant"  />
          <feedback :chain-id="txChains.revoke" :tx="txs.revoke" :err="errs.revoke" />
        </div>
      </section>

      <section class="card highlight">
        <div class="card-header" @click="toggle('rescue')">
          <div>
            <h2>🚨 Rescue Tokens from Storage</h2>
            <p class="card-subtitle">Withdraw tokens accidentally sent to the Storage contract</p>
          </div>
          <span class="chevron">{{ open.rescue ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.rescue" class="card-body">
          <div class="field">
            <label>Token address</label>
            <input v-model="rescue.token" placeholder="e.g. USDC on Polygon: 0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359" />
          </div>
          <div class="field">
            <label>Recipient address</label>
            <input v-model="rescue.to" placeholder="0x..." />
          </div>
          <div class="field">
            <label>Amount (human units, e.g. 114999.5 USDC)</label>
            <input v-model="rescue.amount" type="number" placeholder="0" />
          </div>
          <button class="btn btn-danger" :disabled="busy.rescue" @click="rescueTokens">
            {{ busy.rescue ? 'Sending…' : '🚨 Rescue tokens' }}
          </button>
          <feedback :chain-id="txChains.rescue" :tx="txs.rescue" :err="errs.rescue" />
        </div>
      </section>

      <section class="card">
        <div class="card-header" @click="toggle('transfer')">
          <div>
            <h2>💸 Transfer Tokens from Proxy</h2>
            <p class="card-subtitle">Withdraw tokens from the Proxy contract → Proxy.transferTokens</p>
          </div>
          <span class="chevron">{{ open.transfer ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.transfer" class="card-body">
          <div class="field">
            <label>Token address</label>
            <input v-model="tr.token" placeholder="0x..." />
          </div>
          <div class="field">
            <label>Recipient address</label>
            <input v-model="tr.to" placeholder="0x..." />
          </div>
          <div class="field">
            <label>Amount (human units)</label>
            <input v-model="tr.amount" type="number" placeholder="0" />
          </div>
          <button class="btn btn-primary" :disabled="busy.transfer" @click="transferTokens">
            {{ busy.transfer ? 'Sending…' : 'Send' }}
          </button>
          <feedback :chain-id="txChains.transfer" :tx="txs.transfer" :err="errs.transfer" />
        </div>
      </section>

      <section class="card">
        <div class="card-header" @click="toggle('updateStorage')">
          <div>
            <h2>🔄 Update Storage in Proxy</h2>
            <p class="card-subtitle">Point the Proxy to a new Storage address → Proxy.updateStorage</p>
          </div>
          <span class="chevron">{{ open.updateStorage ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.updateStorage" class="card-body">
          <div class="field">
            <label>New Storage address</label>
            <input v-model="us.address" placeholder="0x..." />
          </div>
          <button class="btn btn-primary" :disabled="busy.updateStorage" @click="updateStorage">
            {{ busy.updateStorage ? '…' : 'Update' }}
          </button>
          <feedback :chain-id="txChains.updateStorage" :tx="txs.updateStorage" :err="errs.updateStorage" />
        </div>
      </section>

      <section class="card">
        <div class="card-header" @click="toggle('ownership')">
          <div>
            <h2>👑 Transfer Ownership</h2>
            <p class="card-subtitle">Change the owner of Storage or Proxy — irreversible</p>
          </div>
          <span class="chevron">{{ open.ownership ? '▲' : '▼' }}</span>
        </div>
        <div v-if="open.ownership" class="card-body">
          <p class="warning">⚠️ After transfer you lose control from current address. Verify the new owner carefully.</p>
          <div class="field">
            <label>New owner address</label>
            <input v-model="own.address" placeholder="0x..." />
          </div>
          <div class="row-btns">
            <button class="btn btn-danger" :disabled="busy.ownerStorage" @click="transferOwnership('storage')">
              {{ busy.ownerStorage ? '…' : 'Transfer Storage ownership' }}
            </button>
            <button class="btn btn-danger" :disabled="busy.ownerProxy" @click="transferOwnership('proxy')">
              {{ busy.ownerProxy ? '…' : 'Transfer Proxy ownership' }}
            </button>
          </div>
          <feedback :chain-id="txChains.ownerStorage" :tx="txs.ownerStorage" :err="errs.ownerStorage" />
          <feedback :chain-id="txChains.ownerProxy" :tx="txs.ownerProxy"   :err="errs.ownerProxy"   />
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js';
import web3, { storageContract, proxyContract, erc20Contract } from '@/web3';

BigNumber.config({ EXPONENTIAL_AT: 1e+9 });

const LS_STORAGE = 'wert_admin_storage_address';
const LS_PROXY   = 'wert_admin_proxy_address';

const CHAIN_NAMES = {
  '0x1': 'Ethereum',
  '0x5': 'Goerli',
  '0x38': 'BSC',
  '0x61': 'BSC Testnet',
  '0x89': 'Polygon',
  '0x2105': 'Base',
  '0x4268': 'Holesky',
  '0xa4b1': 'Arbitrum',
  '0xa869': 'Avalanche Fuji',
  '0xa86a': 'Avalanche',
  '0x13881': 'Polygon Mumbai',
  '0x13882': 'Polygon Amoy',
  '0x14a34': 'Base Sepolia',
  '0x66eee': 'Arbitrum Sepolia',
  '0xaa36a7': 'Sepolia',
};

const CHAIN_EXPLORERS = {
  '0x1': 'https://etherscan.io',
  '0x5': 'https://goerli.etherscan.io',
  '0x38': 'https://bscscan.com',
  '0x61': 'https://testnet.bscscan.com',
  '0x89': 'https://polygonscan.com',
  '0x2105': 'https://basescan.org',
  '0x4268': 'https://holesky.etherscan.io',
  '0xa4b1': 'https://arbiscan.io',
  '0xa869': 'https://testnet.snowtrace.io',
  '0xa86a': 'https://snowtrace.io',
  '0x13881': 'https://mumbai.polygonscan.com',
  '0x13882': 'https://amoy.polygonscan.com',
  '0x14a34': 'https://sepolia.basescan.org',
  '0x66eee': 'https://sepolia.arbiscan.io',
  '0xaa36a7': 'https://sepolia.etherscan.io',
};

function explorerTxUrl(chainId, tx) {
  if (!chainId || !tx) return '';
  const base = CHAIN_EXPLORERS[String(chainId).toLowerCase()];
  return base ? `${base}/tx/${tx}` : '';
}

function getStorage() { return localStorage.getItem(LS_STORAGE) || window.config?.STORAGE_ADDRESS || ''; }
function getProxy()   { return localStorage.getItem(LS_PROXY)   || window.config?.PROXY_ADDRESS   || ''; }

export default {
  name: 'List',

  components: {
    feedback: {
      props: ['tx', 'err', 'chainId'],
      template: `
        <div>
          <div v-if="tx" class="tx-ok">
            ✅ Tx:
            <a v-if="explorerUrl" :href="explorerUrl" target="_blank" rel="noopener noreferrer">{{ tx.slice(0,22) }}…</a>
            <span v-else>{{ tx }}</span>
          </div>
          <div v-if="err" class="tx-err">❌ {{ err }}</div>
        </div>
      `,
      computed: {
        explorerUrl() { return explorerTxUrl(this.chainId, this.tx); },
      },
    },
  },

  data() {
    return {
      account: null,
      chainId: null,
      connecting: false,
      connectError: null,
      open: { settings: false, whitelist: true, rescue: true, transfer: false, updateStorage: false, ownership: false },
      cfg:    { storage: getStorage(), proxy: getProxy() },
      wl:     { address: '', checkResult: null },
      rescue: { token: '', to: '', amount: '' },
      tr:     { token: '', to: '', amount: '' },
      us:     { address: '' },
      own:    { address: '' },
      busy: {},
      txs:  {},
      txChains: {},
      errs: {},
    };
  },

  computed: {
    currentStorage() { return getStorage(); },
    currentProxy()   { return getProxy(); },
    networkLabel() {
      if (!this.chainId) return '';
      const id = String(this.chainId).toLowerCase();
      const decimal = Number.parseInt(id, 16);
      const number = Number.isNaN(decimal) ? id : String(decimal);
      const name = CHAIN_NAMES[id];
      return name ? `${name} (${number})` : `Chain ${number}`;
    },
  },

  async created() {
    if (window.ethereum?.selectedAddress) this.account = window.ethereum.selectedAddress;
    window.ethereum?.on('accountsChanged', ([a]) => { this.account = a || null; });
    window.ethereum?.on('chainChanged', (id) => { this.chainId = id; });
    await this.refreshChainId();
  },

  methods: {
    shortAddr(a) { return a ? a.slice(0, 6) + '…' + a.slice(-4) : '—'; },
    toggle(k) { this.open[k] = !this.open[k]; },

    async refreshChainId() {
      if (!window.ethereum) return null;
      try {
        this.chainId = await window.ethereum.request({ method: 'eth_chainId' });
      } catch {
        this.chainId = null;
      }
      return this.chainId;
    },

    async connectWallet() {
      if (this.connecting) return;
      this.connecting = true;
      this.connectError = null;
      try {
        const existing = await window.ethereum.request({ method: 'eth_accounts' });
        if (existing?.length) {
          this.account = existing[0];
          return;
        }
        const [a] = await window.ethereum.request({ method: 'eth_requestAccounts' });
        this.account = a;
      } catch (e) {
        if (e.code === -32002) {
          this.connectError = 'MetaMask has a pending request. Open MetaMask → find the pending connection → click Cancel, then try again.';
        } else if (e.code === 4001) {
          this.connectError = 'Connection rejected. Click "Connect MetaMask" to try again.';
        } else {
          this.connectError = e.message || String(e);
        }
      } finally {
        this.connecting = false;
      }
    },

    saveConfig() {
      localStorage.setItem(LS_STORAGE, this.cfg.storage);
      localStorage.setItem(LS_PROXY,   this.cfg.proxy);
      window.location.reload();
    },

    async sendTx(key, to, data) {
      this.busy  = { ...this.busy,  [key]: true  };
      this.txs   = { ...this.txs,   [key]: null  };
      this.errs  = { ...this.errs,  [key]: null  };
      try {
        // Pin the chain the tx is submitted on: a later network switch must not
        // repoint an already shown link to another chain's explorer.
        const sentOn = await this.refreshChainId();
        const tx = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [{ from: this.account, to, value: '0x00', data }],
        });
        this.txs = { ...this.txs, [key]: tx };
        this.txChains = { ...this.txChains, [key]: sentOn };
      } catch (e) {
        this.errs = { ...this.errs, [key]: e.message || String(e) };
      } finally {
        this.busy = { ...this.busy, [key]: false };
      }
    },

    async toWei(tokenAddress, humanAmount) {
      const decimalsABI = erc20Contract.methods.decimals().encodeABI();
      const raw = await window.ethereum.request({
        method: 'eth_call',
        params: [{ from: this.account, to: tokenAddress, data: decimalsABI }],
      });
      const decimals = web3.utils.hexToNumber(raw);
      return new BigNumber(humanAmount).multipliedBy(new BigNumber(10).pow(decimals)).toFixed(0);
    },

    async grantAccess() {
      const abi = storageContract.methods.grantAccess(this.wl.address).encodeABI();
      await this.sendTx('grant', getStorage(), abi);
    },

    async revokeAccess() {
      const abi = storageContract.methods.revokeAccess(this.wl.address).encodeABI();
      await this.sendTx('revoke', getStorage(), abi);
    },

    async checkAccess() {
      this.busy = { ...this.busy, check: true };
      this.wl.checkResult = null;
      try {
        const abi = storageContract.methods.checkAccess(this.wl.address).encodeABI();
        const res = await window.ethereum.request({
          method: 'eth_call',
          params: [{ from: this.account, to: getStorage(), data: abi }, 'latest'],
        });
        this.wl.checkResult = web3.utils.hexToNumber(res) === 1;
      } catch (e) {
        this.errs = { ...this.errs, check: e.message };
      } finally {
        this.busy = { ...this.busy, check: false };
      }
    },

    async rescueTokens() {
      this.busy = { ...this.busy, rescue: true };
      this.txs  = { ...this.txs,  rescue: null };
      this.errs = { ...this.errs, rescue: null };
      try {
        const amount = await this.toWei(this.rescue.token, this.rescue.amount);
        const abi    = storageContract.methods.rescueTokens(this.rescue.token, this.rescue.to, amount).encodeABI();
        await this.sendTx('rescue', getStorage(), abi);
      } catch (e) {
        this.errs = { ...this.errs, rescue: e.message || String(e) };
        this.busy = { ...this.busy, rescue: false };
      }
    },

    async transferTokens() {
      this.busy = { ...this.busy, transfer: true };
      this.txs  = { ...this.txs,  transfer: null };
      this.errs = { ...this.errs, transfer: null };
      try {
        const amount = await this.toWei(this.tr.token, this.tr.amount);
        const abi    = proxyContract.methods.transferTokens(this.tr.token, this.tr.to, amount).encodeABI();
        await this.sendTx('transfer', getProxy(), abi);
      } catch (e) {
        this.errs = { ...this.errs, transfer: e.message || String(e) };
        this.busy = { ...this.busy, transfer: false };
      }
    },

    async updateStorage() {
      const abi = proxyContract.methods.updateStorage(this.us.address).encodeABI();
      await this.sendTx('updateStorage', getProxy(), abi);
    },

    async transferOwnership(contract) {
      const key = contract === 'proxy' ? 'ownerProxy' : 'ownerStorage';
      const to  = contract === 'proxy' ? getProxy() : getStorage();
      const abi = contract === 'proxy'
        ? proxyContract.methods.transferOwnership(this.own.address).encodeABI()
        : storageContract.methods.transferOwnership(this.own.address).encodeABI();
      await this.sendTx(key, to, abi);
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 680px;
  margin: 0 auto;
  padding: 24px 16px;
  color: #1a1a1a;
}

/* Header */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.header h1 { font-size: 20px; margin: 0; }
.wallet-status { display: flex; align-items: center; gap: 8px; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; flex-shrink: 0; }
.status-dot.connected    { background: #22c55e; }
.status-dot.disconnected { background: #94a3b8; }
.account-addr { font-size: 13px; font-family: monospace; background: #f1f5f9; padding: 4px 8px; border-radius: 6px; }
.network-chip { font-size: 13px; background: #eef2ff; color: #4338ca; padding: 4px 8px; border-radius: 6px; }

.empty-state { text-align: center; color: #94a3b8; margin-top: 80px; font-size: 16px; }
.info-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  margin: 0;
  text-align: left;
}
.connect-error { color: #f87171; font-size: 13px; margin-top: 8px; max-width: 420px; margin-left: auto; margin-right: auto; line-height: 1.5; }

/* Sections */
.sections { display: flex; flex-direction: column; gap: 10px; }

.card { border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #fff; }
.card.highlight { border-color: #fca5a5; }

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  user-select: none;
  background: #f8fafc;
  gap: 12px;
}
.card.highlight .card-header { background: #fff5f5; }
.card-header:hover { background: #f1f5f9; }
.card-header h2 { margin: 0 0 2px; font-size: 15px; font-weight: 600; }
.card-subtitle { margin: 0; font-size: 12px; color: #94a3b8; font-weight: 400; }
.chevron { font-size: 11px; color: #94a3b8; flex-shrink: 0; }

.card-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 12px; }
.card-footer { padding: 10px 18px; display: flex; gap: 8px; background: #f8fafc; border-top: 1px solid #f1f5f9; }

/* Fields */
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; }
.field input {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  font-family: monospace;
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
}
.field input:focus { border-color: #6366f1; }

/* Buttons */
.row-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.btn {
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;
  white-space: nowrap;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary   { background: #6366f1; color: #fff; }
.btn-primary:not(:disabled):hover   { background: #4f46e5; }
.btn-success   { background: #22c55e; color: #fff; }
.btn-success:not(:disabled):hover   { background: #16a34a; }
.btn-danger    { background: #ef4444; color: #fff; }
.btn-danger:not(:disabled):hover    { background: #dc2626; }
.btn-secondary { background: #e2e8f0; color: #1a1a1a; }
.btn-secondary:not(:disabled):hover { background: #cbd5e1; }

/* Results */
.result { padding: 8px 12px; border-radius: 8px; font-size: 14px; }
.result.ok  { background: #f0fdf4; color: #166534; }
.result.err { background: #fef2f2; color: #991b1b; }
.warning { background: #fff7ed; border: 1px solid #fed7aa; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #92400e; margin: 0; }

.addr-chip { background: #f1f5f9; border-radius: 6px; padding: 4px 10px; font-size: 12px; font-family: monospace; color: #475569; }
</style>

<style>
.tx-ok { margin-top: 6px; font-size: 13px; color: #166534; background: #f0fdf4; padding: 6px 10px; border-radius: 6px; word-break: break-all; }
.tx-ok a { color: #166534; }
.tx-err { margin-top: 6px; font-size: 13px; color: #991b1b; background: #fef2f2; padding: 6px 10px; border-radius: 6px; word-break: break-all; }
</style>
